<template>
  <section>
    <Accordion type="multiple" :default-value="['basic', 'financial', 'dates']" class="tw-w-full">
      <!-- Basic Information -->
      <AccordionItem value="basic" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <Info :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.basic_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow :label="t('payment.titles.payment_no')" :value="payment.payment_number" />
            <DetailRow
              :label="t('suppliers.titles.customer')"
              :value="payment.customer?.name || '-'"
              :to="
                payment.customer?.id
                  ? { name: 'view-customer', params: { id: payment.customer.id } }
                  : undefined
              "
            />
            <DetailRow
              :label="t('requests.titles.project')"
              :value="payment.project?.name || '-'"
              :to="
                payment.project?.id
                  ? { name: 'edit-project', params: { id: payment.project.id } }
                  : undefined
              "
            />
            <DetailRow
              :label="t('orders.titles.supplier')"
              :value="
                payment.supplier?.is_deleted
                  ? `${payment.supplier?.name} (${t('suppliers.labels.deleted')})`
                  : payment.supplier?.name || '-'
              "
              :to="
                payment.supplier?.id && !payment.supplier?.is_deleted
                  ? { name: 'edit-supplier', params: { supplierId: payment.supplier.id } }
                  : undefined
              "
              :class="payment.supplier?.is_deleted ? 'tw-text-secondary-300' : ''"
            />
            <DetailRow
              :label="t('orders.titles.supplier_no')"
              :value="payment.supplier?.supplier_number || '-'"
            />
            <DetailRow
              :label="t('orders.titles.created_by')"
              :value="payment.created_by?.display_name || '-'"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Financial Information -->
      <AccordionItem value="financial" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <Wallet :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.financial_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="payment.net_eur"
              :label="t('orders.titles.amount_net')"
              :value="payment.net_eur ? n(+payment.net_eur, 'currency') : '-'"
            />
            <DetailRow
              :label="t('orders.titles.amount_gross')"
              :value="payment.amount ? n(+payment.amount, 'currency') : '-'"
            />
            <DetailRow
              :label="t('payments.titles.remaining_project_balance')"
              :value="
                payment.remaining_project_balance
                  ? n(+payment.remaining_project_balance, 'currency')
                  : '-'
              "
            />
            <DetailRow
              :label="t('payments.titles.remaining_order_balance')"
              :value="
                payment.remaining_order_balance
                  ? n(+payment.remaining_order_balance, 'currency')
                  : '-'
              "
            />
            <DetailRow
              v-if="payment.discount"
              :label="t('payment.titles.discount')"
              :value="n(+payment.discount, 'currency')"
            />
            <DetailRow
              v-if="payment.gross_eur_less_discount"
              :label="t('payment.titles.gross_amount_after_discount')"
              :value="n(+payment.gross_eur_less_discount, 'currency')"
            />
            <DetailRow
              v-if="payment.comparison_gross_eur_order_list"
              :label="t('payment.titles.gross_comparison_amount')"
              :value="n(+payment.comparison_gross_eur_order_list, 'currency')"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Dates -->
      <AccordionItem value="dates" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <Calendar :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.dates') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              :label="t('orders.titles.created_on')"
              :value="moment(payment.created_on).format('DD.MM.YYYY')"
            />
            <DetailRow
              v-if="payment.payment_date"
              :label="t('payment.titles.payment_date')"
              :value="moment(payment.payment_date).format('DD.MM.YYYY')"
            />
            <DetailRow
              v-if="payment.invoice_date"
              :label="t('payment.titles.invoice_date')"
              :value="moment(payment.invoice_date).format('DD.MM.YYYY')"
            />
            <DetailRow
              v-if="payment.due_date"
              :label="t('payment.titles.due_date')"
              :value="moment(payment.due_date).format('DD.MM.YYYY')"
            />
            <DetailRow
              v-if="payment.service_date"
              :label="t('orders.titles.service_date')"
              :value="moment(payment.service_date).format('DD.MM.YYYY')"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Invoice Details -->
      <AccordionItem v-if="hasInvoiceDetails" value="invoice" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <ReceiptText :class="iconClass" />
            <span :class="labelClass">{{ t('payment.titles.invoice_details') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <div
              v-if="payment.orders?.length"
              class="tw-flex tw-justify-between tw-items-start tw-gap-6 tw-py-2.5 tw-border-b tw-border-surface-100 dark:tw-border-neutral-800/60"
            >
              <dt class="tw-text-sm tw-text-surface-500 dark:tw-text-neutral-400 tw-shrink-0">
                {{ t('payment.titles.sequence_order_no') }}
              </dt>
              <dd class="tw-text-sm tw-font-semibold tw-text-right">
                <template v-for="(order, idx) in payment.orders" :key="order.order_id || idx">
                  <router-link
                    v-if="order.order_id"
                    :to="{ name: 'order', params: { id: order.order_id } }"
                    class="tw-text-secondary-700 hover:tw-text-secondary-900 dark:tw-text-neutral-200 hover:tw-underline tw-underline-offset-2"
                  >
                    {{ order.order_number }}
                  </router-link>
                  <span v-else class="tw-text-secondary-900 dark:tw-text-neutral-100">
                    {{ order.order_number }}
                  </span>
                  <span v-if="idx < payment.orders.length - 1" class="tw-text-surface-400">,</span>
                </template>
              </dd>
            </div>
            <DetailRow
              v-if="payment.payment_term"
              :label="t('payments.titles.payment_terms')"
              :value="payment.payment_term"
            />
            <DetailRow
              v-if="payment.invoice_no"
              :label="t('payment.titles.invoice_no')"
              :value="payment.invoice_no"
            />
            <DetailRow
              v-if="payment.masse_liability"
              :label="t('payment.titles.masse_liability')"
              :value="payment.masse_liability"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Recurring Info -->
      <AccordionItem v-if="payment.is_recurring" value="recurring" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <RefreshCw :class="iconClass" />
            <span :class="labelClass">{{ t('orders.recurring.recurring_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="payment.name"
              :label="t('payment.titles.payment_name')"
              :value="payment.name"
            />
            <DetailRow
              v-if="payment.frequency"
              :label="t('orders.recurring.frequency')"
              :value="payment.frequency"
            />
            <DetailRow
              v-if="payment.end_after !== null"
              :label="t('orders.recurring.ends_after')"
              :value="payment.end_after !== null ? String(payment.end_after) : '0'"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Comments -->
      <AccordionItem v-if="hasComments" value="comments" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <MessageSquareText :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.order_comments') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="payment.comment"
              :label="t('orders.titles.comment')"
              :value="payment.comment"
              :multiline="true"
            />
            <DetailRow
              v-if="payment.comment_allea"
              :label="t('orders.titles.comment_allea')"
              :value="payment.comment_allea"
              :multiline="true"
            />
            <DetailRow
              v-if="payment.comment_management"
              :label="t('orders.titles.comment_management')"
              :value="payment.comment_management"
              :multiline="true"
            />
            <DetailRow
              v-if="payment.comment_insv"
              :label="t('orders.titles.comment_insv')"
              :value="payment.comment_insv"
              :multiline="true"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Approval Info -->
      <AccordionItem
        v-if="hasApprovalInfo"
        value="approval"
        :class="sectionClass + ' last:tw-border-b-0'"
      >
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <ShieldCheck :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.approval_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="payment.status === 'APPROVED' && payment.approved_by?.display_name"
              :label="t('orders.titles.approved_by')"
              :value="payment.approved_by.display_name"
            />
            <DetailRow
              v-if="payment.status === 'REJECTED' && payment.rejected_by?.display_name"
              :label="t('orders.titles.rejected_by')"
              :value="payment.rejected_by.display_name"
            />
            <div v-if="payment?.cleared_by?.length" class="tw-flex tw-flex-col tw-gap-2 tw-pt-2.5">
              <span class="tw-text-sm tw-text-surface-500 dark:tw-text-neutral-400">
                {{ t('orders.titles.cleared_by') }}
              </span>
              <div class="tw-flex tw-flex-wrap tw-gap-2">
                <Badge
                  v-for="(clearer, index) in payment.cleared_by"
                  :key="index"
                  variant="secondary"
                  class="tw-text-xs"
                >
                  {{ clearer }}
                </Badge>
              </div>
            </div>
          </dl>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import {
  Calendar,
  Info,
  MessageSquareText,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from 'lucide-vue-next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { DetailRow } from '@/components/ui/detail-row';

const sectionClass =
  'tw-border-b tw-border-surface-200 dark:tw-border-neutral-800 last:tw-border-b-0';
const triggerClass =
  'tw-py-3.5 hover:tw-no-underline [&[data-state=open]>svg]:tw-text-secondary-700';
const iconClass = 'tw-h-[1.05rem] tw-w-[1.05rem] tw-text-surface-400 tw-shrink-0';
const labelClass =
  'tw-font-heading tw-text-sm tw-font-semibold tw-text-secondary-800 dark:tw-text-neutral-200';

const { t, n } = useI18n();

const props = defineProps({
  payment: {
    type: Object,
    default: () => ({}),
  },
});

const hasInvoiceDetails = computed(() => {
  return (
    props.payment.orders?.length ||
    props.payment.payment_term ||
    props.payment.invoice_no ||
    props.payment.masse_liability
  );
});

const hasComments = computed(() => {
  return (
    props.payment.comment ||
    props.payment.comment_allea ||
    props.payment.comment_management ||
    props.payment.comment_insv
  );
});

const hasApprovalInfo = computed(() => {
  return (
    props.payment.status === 'APPROVED' ||
    props.payment.status === 'REJECTED' ||
    (props.payment?.cleared_by?.length ?? 0) > 0
  );
});
</script>
